/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { PropTypes } from './Timer.types';
import type { ReactElement } from 'react';

import { Flex, Text } from '@rango-dev/whale';
import React, { Fragment, useEffect, useState } from 'react';

import styles from './Timer.module.css';

const TIMER_UNITS = ['days', 'hours', 'minutes', 'seconds'] as const;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * 60;
const SECONDS_IN_DAY = 60 * 60 * 24;
const LAST_UNIT_INDEX = 3;
const PADDED_VALUE_THRESHOLD = 9;

export function Timer(props: PropTypes): ReactElement {
  const { start, end } = props;
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const startTime = new Date(start).getTime();
      const endTime = new Date(end).getTime();

      const calculateTimeLeft = (diff: number) => {
        const days = Math.floor(diff / (1000 * SECONDS_IN_DAY));
        const hours = Math.floor((diff % (1000 * SECONDS_IN_DAY)) / (1000 * SECONDS_IN_HOUR));
        const minutes = Math.floor((diff % (1000 * SECONDS_IN_HOUR)) / (1000 * SECONDS_IN_MINUTE));
        const seconds = Math.floor((diff % (1000 * SECONDS_IN_MINUTE)) / 1000);
        setTimeLeft({ days, hours, minutes, seconds });
      };

      if (now < startTime) {
        calculateTimeLeft(startTime - now);
      } else if (now >= startTime && now < endTime) {
        calculateTimeLeft(endTime - now);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [start, end]);

  return (
    <Flex align="center" gap="1" role="timer">
      {TIMER_UNITS.map((unit, index) => {
        const value = timeLeft[unit];
        return (
          <Fragment key={unit}>
            <Flex
              align="center"
              justify="center"
              gap="1"
              flexShrink="0"
              px="2"
              py="1"
              className={styles.timeBox}>
              <Text as="span" size="4" weight="bold" className={styles.timeValue}>
                {value > PADDED_VALUE_THRESHOLD ? value : `0${value}`}
              </Text>
              <Text as="span" size="1" weight="medium" className={styles.timeUnit}>
                {unit.charAt(0)}
              </Text>
            </Flex>
            {index < LAST_UNIT_INDEX && (
              <Text as="span" size="4" weight="bold" className={styles.separator}>
                :
              </Text>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
}
